import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Monitor,
  MapPin,
  Tv,
  FileImage,
  ShoppingBag,
  Menu,
  List,
  Shuffle,
  LayoutDashboard,
  LogOut,
  UserPlus,
  Users,
  Shield,
  Eye,
  Music,
  ChevronLeft,
  ChevronRight,
  Activity,
  Clock,
  Calendar,
  DollarSign,
  Sun,
  Moon,
  Building2
} from 'lucide-react';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/live-preview', icon: Eye, label: 'Live Preview' },
  { path: '/locations', icon: MapPin, label: 'Locații' },
  { path: '/screens', icon: Tv, label: 'Ecrane' },
  { path: '/content', icon: FileImage, label: 'Conținut' },
  { path: '/screen-sync', icon: Shuffle, label: 'Sincronizare' },
  { path: '/happy-hour', icon: ChevronRight, label: 'Happy Hour' },
  { path: '/playlists', icon: List, label: 'Playlist-uri' },
  { path: '/products', icon: ShoppingBag, label: 'Produse' },
  { path: '/digital-menus', icon: Menu, label: 'Meniuri Digitale' },
  { path: '/audio', icon: Music, label: 'Muzică & Reclame' },
  { path: '/brands', icon: List, label: 'Brandurile noastre' },
];

const adminMenuItems = [
  { path: '/organizations', icon: Building2, label: 'Clienți' },
  { path: '/users', icon: Users, label: 'Utilizatori' },
  { path: '/invitations', icon: UserPlus, label: 'Invitații' },
  { path: '/activity-logs', icon: Activity, label: 'Jurnale Activitate' },
  { path: '/billing', icon: DollarSign, label: 'Facturare' },
];

export const DashboardLayout = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [theme, setTheme] = useState(() => localStorage.getItem('app-theme') || 'red');
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('app-dark-mode') === 'true');
  const [currentTime, setCurrentTime] = useState(new Date());
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isSuperAdmin, selectedOrgId, selectOrganization, organizations, isTenantPortal, tenantOrgId } = useAuth();

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('app-dark-mode', isDarkMode);
  }, [isDarkMode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (date) => {
    const days = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
    const months = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className={`${isSidebarCollapsed ? 'w-20' : 'w-[17rem]'} h-screen fixed left-0 top-0 z-50 flex flex-col transition-all duration-300 ease-in-out border-r border-slate-200/80 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-800/50`} data-testid="sidebar">
        <div className={`px-3 border-b border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-900 relative flex items-center justify-center h-[72px] shrink-0`}>
          <Link to="/dashboard" className="flex items-center justify-center overflow-hidden w-full min-w-0">
            {isSidebarCollapsed ? (
              <img
                src="/favicon.png?v=2"
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            ) : (
              <div className="bg-white px-3 py-1.5 rounded-xl shadow-sm border border-slate-100">
                <img
                  src="/logo_smart_display.png?v=2"
                  alt="Logo"
                  className="h-10 object-contain max-w-full"
                />
              </div>
            )}
          </Link>
          <button
             onClick={toggleSidebar}
             className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-full flex items-center justify-center shadow-md text-slate-500 dark:text-slate-400 hover:bg-[#7c3aed] hover:border-[#7c3aed] hover:text-white transition-colors z-[60]"
          >
             {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto hide-scrollbar">
          {menuItems.filter(item => {
            if (user?.role === 'manager') {
              // Limited set for manager
              const managerPaths = ['/dashboard', '/live-preview', '/locations', '/screens'];
              return managerPaths.includes(item.path);
            }
            return true;
          }).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'sidebar-link-active' : ''} ${isSidebarCollapsed ? 'justify-center' : ''}`}
                data-testid={`nav-${item.path.substring(1)}`}
                title={isSidebarCollapsed ? item.label : ''}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!isSidebarCollapsed && <span className="animate-in fade-in slide-in-from-left-2 duration-300">{item.label}</span>}
              </Link>
            );
          })}

          {/* Admin Menu Items */}
          {isAdmin() && (
            <>
              <div className="pt-4 pb-2">
                <div className="flex items-center gap-2 px-3 text-xs font-semibold text-slate-400 uppercase">
                  <Shield className="w-3 h-3 text-slate-400" />
                  Admin
                </div>
              </div>
              {adminMenuItems
                .filter((item) => {
                  // Only Super Admin on central platform can manage Organizations/Clients and Billing
                  if (item.path === '/organizations' || item.path === '/billing') {
                    return isSuperAdmin() && !isTenantPortal;
                  }
                  return true;
                })
                .map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`sidebar-link ${isActive ? 'sidebar-link-active' : ''} ${isSidebarCollapsed ? 'justify-center' : ''}`}
                      data-testid={`nav-${item.path.substring(1)}`}
                      title={isSidebarCollapsed ? item.label : ''}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      {!isSidebarCollapsed && <span className="animate-in fade-in slide-in-from-left-2 duration-300">{item.label}</span>}
                    </Link>
                  );
                })}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-200/80 dark:border-slate-700/80">
          {!isSidebarCollapsed && (
            <div className="rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-sm border border-slate-200/80 dark:border-slate-700/80 p-4 mb-3 animate-in fade-in duration-300 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs text-slate-400">Autentificat ca</p>
                {isSuperAdmin() && (
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded">
                    Admin
                  </span>
                )}
                {user?.role === 'manager' && (
                  <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-medium rounded">
                    Manager
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              <div className="mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-800 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 truncate flex items-center gap-1">
                <span>🏢</span>
                <span className="truncate">
                  {isTenantPortal
                    ? (organizations.find(o => o.id === tenantOrgId)?.name || 'Sushi Han')
                    : isSuperAdmin()
                      ? (selectedOrgId && selectedOrgId !== 'all'
                          ? (organizations.find(o => o.id === selectedOrgId)?.name || selectedOrgId)
                          : 'Toate Organizațiile')
                      : (user?.organization_name || user?.organization_id || 'Sushi Master')}
                </span>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-colors duration-200 ${isSidebarCollapsed ? 'justify-center p-3' : 'gap-2 px-4 py-3'}`}
            data-testid="logout-button"
            title={isSidebarCollapsed ? "Deconectare" : ""}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!isSidebarCollapsed && <span className="animate-in fade-in duration-300">Deconectare</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`${isSidebarCollapsed ? 'ml-20' : 'ml-[17rem]'} flex-1 min-h-screen transition-all duration-300 ease-in-out flex flex-col`}>
        {/* Top Header Bar */}
        <header className="sticky top-0 z-40 px-8 h-[72px] flex items-center border-b border-slate-200/80 dark:border-slate-700/80 shrink-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md">
          <div className="flex items-center justify-between gap-4 w-full">
            {/* Organization Switcher */}
            {isTenantPortal ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-1.5 shadow-sm">
                  <Building2 className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 hidden sm:inline">Portal Client:</span>
                  <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                    {organizations.find(o => o.id === tenantOrgId)?.name || 'Sushi Han'}
                  </span>
                  <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/60 px-2 py-0.5 rounded-full uppercase">
                    Dedicat
                  </span>
                  {isSuperAdmin() && (
                    <a
                      href="https://smr.onl"
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-semibold ml-2 border-l border-amber-300 dark:border-amber-700 pl-2 hidden md:inline"
                      title="Mergi la panoul central Super Admin cu toate organizațiile"
                    >
                      Panou Central (Toate) ↗
                    </a>
                  )}
                </div>
              </div>
            ) : isSuperAdmin() ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700 rounded-xl px-3 py-1.5 shadow-sm">
                  <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 hidden sm:inline">Client:</span>
                  <select
                    value={selectedOrgId || 'all'}
                    onChange={(e) => {
                      selectOrganization(e.target.value);
                      window.location.reload();
                    }}
                    className="bg-transparent text-sm font-bold text-slate-800 dark:text-slate-200 focus:outline-none cursor-pointer pr-1"
                  >
                    <option value="all">🌐 Toate Organizațiile</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        🏢 {org.name} ({org.screens_count || 0} ecrane)
                      </option>
                    ))}
                  </select>
                  <Link
                    to="/organizations"
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-semibold ml-1 hidden md:inline border-l border-slate-200 dark:border-slate-700 pl-2"
                    title="Gestionează organizațiile"
                  >
                    Setări
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700 rounded-xl px-3 py-1.5 shadow-sm">
                <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Organizație:</span>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                  {user?.organization_name || 'Sushi Master'}
                </span>
              </div>
            )}

            <div className="flex items-center gap-4">
              {/* Date & Time */}
              <div className="flex items-center gap-3 text-right">
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDate(currentTime)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-bold text-slate-700 dark:text-slate-300 justify-end">
                  <Clock className="w-3.5 h-3.5 text-brand-400" />
                  <span className="tabular-nums">{formatTime(currentTime)}</span>
                </div>
              </div>
            </div>

            {/* Version / Build Date – only for Super Admin */}
            {isSuperAdmin() && (
              <div className="px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <span className="text-[10px] font-mono text-slate-500 dark:text-slate-400">
                  v{process.env.REACT_APP_BUILD_DATE || 'DEV'}
                </span>
              </div>
            )}

            {/* Theme Controls */}
            <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-100/50 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm">
              <button
                onClick={() => setTheme('red')}
                className={`w-5 h-5 rounded-full bg-rose-500 transition-all ${theme === 'red' ? 'ring-2 ring-offset-1 ring-rose-500 shadow-sm scale-110' : 'hover:scale-110 opacity-70 hover:opacity-100'}`}
                title="Tema Roșie"
              />
              <button
                onClick={() => setTheme('blue')}
                className={`w-5 h-5 rounded-full bg-blue-500 transition-all ${theme === 'blue' ? 'ring-2 ring-offset-1 ring-blue-500 shadow-sm scale-110' : 'hover:scale-110 opacity-70 hover:opacity-100'}`}
                title="Tema Albastră"
              />
              <button
                onClick={() => setTheme('green')}
                className={`w-5 h-5 rounded-full bg-emerald-500 transition-all ${theme === 'green' ? 'ring-2 ring-offset-1 ring-emerald-500 shadow-sm scale-110' : 'hover:scale-110 opacity-70 hover:opacity-100'}`}
                title="Tema Verde"
              />
              <button
                onClick={() => setTheme('purple')}
                className={`w-5 h-5 rounded-full bg-violet-600 transition-all ${theme === 'purple' ? 'ring-2 ring-offset-1 ring-violet-600 shadow-sm scale-110' : 'hover:scale-110 opacity-70 hover:opacity-100'}`}
                title="Tema Violet"
              />
              <div className="w-px h-4 bg-slate-300 mx-0.5" />
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-1 rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:text-slate-200 dark:text-slate-300 transition-colors"
                title="Toggle Dark Mode"
              >
                {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>

            {/* Separator */}
            <div className="w-px h-8 bg-slate-200" />

            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 leading-tight">{user?.full_name}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold">
                  {isSuperAdmin() ? 'Admin' : user?.role === 'manager' ? 'Manager' : 'Admin'}
                </p>
              </div>
              {/* Avatar */}
              {user?.avatar_url ? (
                <img
                  src={
                    user.avatar_url.startsWith('http')
                      ? user.avatar_url
                      : `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}${user.avatar_url}`
                  }
                  alt={user?.full_name}
                  className="w-9 h-9 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700 shadow-sm"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white text-xs font-bold shadow-sm border-2 border-slate-200 dark:border-slate-700">
                  {getInitials(user?.full_name)}
                </div>
              )}
            </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};
